import React from "react";
import * as ReactDOM from "react-dom";

/**
 * Load the real F7 bundle.
 *
 * The bundle is an esbuild IIFE that ends with `window.F7 = …` and reads
 * `window.React` for its element factory — so it is a CLASSIC script with a
 * hard ordering requirement, not an ES module. Two consequences:
 *
 *   1. React must be on `window` BEFORE the script evaluates. A module import
 *      cannot guarantee that, so the script is injected here instead.
 *   2. Nothing may render an F7 component until this resolves. `main.tsx`
 *      awaits it next to the config fetch, which it was already waiting on.
 *
 * REACT-DOM TOO, and it is not optional. The bundle carries a
 * `shim:react-dom-shim` module whose whole body is:
 *
 *     var D9 = window.ReactDOM;
 *     module.exports = Object.assign({ preload: n16, ...no-op stubs }, D9);
 *
 * `Object.assign({...}, undefined)` does NOT throw — it returns just the
 * stubs. So without this assignment the shim resolves to an object with no
 * `createPortal`, and every portal-based F7 component (the confirm dialog,
 * dropdowns, tooltips — three call sites in the bundle) dies on
 * `(0, import_react_dom5.createPortal)(...)` is not a function. The failure
 * lands at the moment a dialog opens, not at load, which is why it read as a
 * page bug rather than a missing global.
 *
 * Vendored at `public/ds/_ds_bundle.js` rather than pulled from a CDN: this is
 * an internal console behind Cognito, and a design system that can fail to load
 * over the network is a console that renders unstyled during an incident.
 */
declare global {
  interface Window {
    React?: typeof React;
    ReactDOM?: typeof ReactDOM;
    F7?: Record<string, unknown>;
  }
}

const SRC = "/ds/_ds_bundle.js";

let pending: Promise<void> | null = null;

export function loadF7(): Promise<void> {
  if (window.F7) return Promise.resolve();
  if (pending) return pending;

  pending = new Promise<void>((resolve, reject) => {
    // Must be assigned before the script runs, not after.
    window.React = React;
    window.ReactDOM = ReactDOM;

    const el = document.createElement("script");
    el.src = SRC;
    el.async = false;
    el.onload = () => {
      if (window.F7) resolve();
      else reject(new Error(`${SRC} loaded but did not define window.F7.`));
    };
    el.onerror = () =>
      reject(
        new Error(
          `Could not load the F7 design system from ${SRC}. It is vendored at ` +
            `web/public/ds/_ds_bundle.js — check the file is present and that the ` +
            `deploy uploaded it.`,
        ),
      );
    document.head.appendChild(el);
  });

  return pending;
}
